import axios from 'axios';
import jscookie from 'js-cookie';

const apiUrl = process.env.CLIENT_API_URL;
const isDev = process.env.NODE_ENV !== 'production';

export const client = axios.create({
  baseURL: apiUrl,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
  },
})

// inject X-Sumo-Auth from the cookie; warn (in dev) about legacy call sites still
// passing it manually via getRequestConfig() so we can migrate them off.
client.interceptors.request.use(config => {
  if (isDev && config.headers && config.headers['X-Sumo-Auth'] !== undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      `[apiClient] ${config.method?.toUpperCase() || 'GET'} ${config.url}: ` +
      'X-Sumo-Auth is being passed manually (likely via `...getRequestConfig()`). ' +
      'The interceptor now handles this — drop `...getRequestConfig()` from the call.',
    );
  }
  config.headers['X-Sumo-Auth'] = jscookie.get('__smToken') || '';
  return config;
});

/**
 * Fetch-based equivalent of `client` for callers that want fetch semantics.
 *
 * - Prepends CLIENT_API_URL to relative paths (full URLs pass through).
 * - Injects X-Sumo-Auth from the __smToken cookie.
 * - Defaults to POST with JSON Content-Type.
 * - Accepts `body` as a string (passed through) or object (auto-stringified).
 * - Returns the parsed JSON response.
 */
export const apiFetch = async (path, options = {}) => {
  const { query, body, headers: userHeaders, ...rest } = options;
  const token = jscookie.get('__smToken');
  const headers = {
    'Content-Type': 'application/json',
    ...(userHeaders || {}),
    ...(token ? { 'X-Sumo-Auth': token } : {}),
  };
  const init = {
    ...rest,
    credentials: 'omit',
    headers,
    method: rest.method || 'POST',
  };
  if (body !== undefined) {
    init.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  const isAbsolute = /^https?:|^\/\//.test(path);
  let url = isAbsolute ? path : `${apiUrl}${path}`;
  if (query) {
    const params = new URLSearchParams(
      Object.entries(query).reduce((acc, [k, v]) => {
        if (v !== undefined && v !== null) acc[k] = v;
        return acc;
      }, {}),
    );
    const qs = params.toString();
    if (qs) url = `${url}?${qs}`;
  }

  const response = await fetch(url, init);
  return response.json();
};
